import { IUser } from "./../Auth"
import { ITag } from "../Tags"
import { IListResponse, XHRInstance } from "@sembark-travel/xhr"
import { TTeam } from "../Teams"
import { useCallback } from "react"
import { useXHR } from "@sembark-travel/xhr"
import useSWR from "swr"
import { queryToSearch } from "@sembark-travel/ui-router"
import { useNavigate } from "../router"
import { ITripSource } from "../TripSources"
import { TTripDestination } from "../TripDestinations"
import { IComment } from "../Comments"
import { IContact } from "../Contacts"
import { TLeadChannel } from "../LeadChannels"
import { IHotel } from "../Hotels"
import { ICountry } from "../Locations"
import { IAddress } from "../Addresses"
import { ITrip } from "../Trips"

export interface ITripPlanRequest {
	id: number
	name: string
	phone_number: string
	email?: string
	destination?: string
	start_date?: string
	no_of_days?: number
	no_of_adults?: number
	no_of_children?: number
	hotel_preference?: string
	comments?: string
	trip_id?: number
	is_manually_linked_to_trip: 0 | 1
	children?: Array<{ count: number; age: number }>
	owner_id?: number
	owner?: IUser
	assigned_at?: string
	assigned_by?: IUser
	created_at: string
	reference_id?: string
	trip_source?: ITripSource
	trip_source_contact?: IContact
	tags?: Array<ITag>
	notes?: IComment[]
	extra_fields?: Record<string, unknown>
	is_ai_generated?: boolean
	mail_messages_count?: number
	wa_messages_count?: number
	linked_mail_threaded_trips?: Array<{
		id: number
		status: string
		current_status: string
	}>
	deleted_at?: string
	deleted_by?: IUser
	lead_channel?: TLeadChannel
	lead_purpose?: ILeadPurpose
	hotels_of_interest?: IHotel[]
	has_past_interactions?: {
		calls: boolean
		mails: boolean
	}
	lead_close_reason?: { id: number; name: string }
}

type TParameterType = "string" | "integer"

export interface TParameter {
	name: string
	display_name?: string
	operator?: TOperator
	attribute: string
	type: TParameterType
	values?: string[] // optional, since some parameters don't have values
}

type TRightOperandType =
	| "integer"
	| "array<integer>"
	| "string"
	| "array<string>"

export interface TOperator {
	name: string
	right_operand_type: TRightOperandType
}

export interface TOperatorGroup {
	left_operand_type: "integer" | "string" | "any"
	operators: TOperator[]
}

export interface TRuleConfig {
	data: {
		parameters: TParameter[]
		operators: TOperatorGroup[]
	}
}

export interface TAnalysisParameters {
	data: { display_name: string; name: string; values?: string[] }[]
}

export interface TAppliedRules {
	data: {
		grouped_rule_items: {
			right_operand_value: string
			group_id: number
			id: number
			parameter: TParameter & { operator: TOperator }
			description: string
		}[][]
		id: number
		teams: TTeam[]
		users: IUser[]
		name: string
		priority: number
	}[]
}
export function AutoDistributionXHR(xhr: XHRInstance) {
	return {
		async getRoutingRules(params?: unknown): Promise<TRuleConfig> {
			return xhr
				.get("/routing-rules/create", { params })
				.then((resp) => resp.data)
		},
		async getAppliedRules(params?: unknown): Promise<TAppliedRules> {
			return xhr.get("/routing-rules", { params }).then((resp) => resp.data)
		},
		async getAnalysisParameters(
			params?: unknown
		): Promise<TAnalysisParameters> {
			return xhr
				.get("/routing-rules-analysis-parameters", { params })
				.then((resp) => resp.data)
		},
	}
}

export type TLeadReportStats = {
	archived: number
	moved_to_trips: number
	pending_followups: number
	total: number
	active_followups: number
	ai_generated?: number
}

export interface ILeadPurpose {
	id: number
	name: string
	config: {
		close_lead: boolean | "conditional"
		move_to_trips: boolean | "conditional"
		hotels_of_interest: boolean | "conditional"
		link_to_existing_trip: boolean | "conditional"
		new_lead: boolean | "conditional"
		travel_dates: boolean | "conditional"
	}
}

export function useLeadReportStats({
	params,
}: {
	params: Record<string, unknown>
}) {
	const xhr = useXHR()
	const { page, ...otherParams } = params
	const { data: stats, error } = useSWR(
		`/trip-plan-request-report/stats?${queryToSearch(otherParams)}`,
		() =>
			xhr
				.get<{ data: TLeadReportStats }>("/trip-plan-request-report/stats", {
					params: {
						...params,
					},
				})
				.then((resp) => resp.data.data)
	)
	return { stats, error }
}
export const LeadStatusType = [
	{ name: "Moved to Trips", id: "moved" },
	{ name: "Active Followup", id: "active_followups" },
	{ name: "Pending Followup", id: "pending_followups" },
	{ name: "Closed", id: "archived" },
]

export function useLeadRedirect<T>() {
	const navigate = useNavigate()

	const onRedirect = useCallback(
		(query: T) => {
			if (query) {
				navigate({
					pathname: "/trip-plan-requests",
					search: queryToSearch(query),
				})
			}
		},
		[navigate]
	)

	return { onRedirect }
}

export function LeadXHR(xhr: XHRInstance) {
	return {
		async getLeads(params?: unknown): Promise<IListResponse<ITripPlanRequest>> {
			return xhr
				.get("/trip-plan-requests", { params })
				.then((resp) => resp.data)
		},
		async getLeadSources(): Promise<Array<{ id: string; name: string }>> {
			return xhr
				.get("/trip-plan-request-sources")
				.then((resp) => resp.data.data)
		},
		async createLead(data: unknown): Promise<ITripPlanRequest> {
			return xhr.post("/leads", data).then((resp) => resp.data.data)
		},
		async restoreLead(id: string | number): Promise<{ message: string }> {
			return xhr
				.patch(`/trip-plan-requests/${id}/restore`)
				.then((resp) => resp.data)
		},
		async assignLeadOwner(
			requestIds: Array<number>,
			ownerId?: number | null
		): Promise<IListResponse<ITripPlanRequest>> {
			return xhr
				.post("/trip-plan-request-owners", {
					items: requestIds,
					owner_id: ownerId,
				})
				.then((resp) => resp.data)
		},
		async getPayloadForTrip(requestId: number | string) {
			return xhr
				.get<{
					data: {
						trip_source: ITripSource
						trip_source_contact: IContact | null
						reference_id: string | null
						destinations: Array<TTripDestination> | null
						is_manually_linked_to_trip: 0 | 1
						start_date: string | null
						no_of_days: number
						no_of_nights: number
						no_of_adults: number
						children: Array<{ age: number; count: number }> | null
						tourist?: {
							salutation: "Mr." | "Mrs." | "Ms." | null
							name: string
							email: string | null
							phone_numbers: Array<string> | null
							nationality?: ICountry
							address?: IAddress
						}
						comments: string | null
						lead_channel: TLeadChannel | null
						lead_purpose: ILeadPurpose | null
						hotels_of_interest: IHotel[] | null
						sales_team: IUser
						trip?: ITrip
						can_create_new: boolean
						suggested_trips?: ITrip[]
					}
				}>(`/trip-plan-requests/${requestId}/trip/create`)
				.then((resp) => resp.data.data)
		},
		async getLeadDestinations(
			params?: unknown
		): Promise<Array<{ id: string; name: string }>> {
			return xhr
				.get("/trip-plan-request-destinations", { params })
				.then((resp) => resp.data.data)
		},

		async editTagsAndFollowUps(
			requestId: number | string,
			{
				tags,
				note,
				due_at,
				wa_message_template_id,
				wa_phone_number_id,
			}: {
				tags?: Array<string>
				note?: string
				due_at?: string
				/**
				 * A WhatsApp template sent to this lead when the follow-up
				 * falls due, if it is still unresolved by then.
				 */
				wa_message_template_id?: number
				wa_phone_number_id?: number
			}
		): Promise<ITripPlanRequest> {
			return xhr
				.post(`/trip-plan-requests/${requestId}/tags-and-followups`, {
					tags,
					note,
					due_at,
					wa_message_template_id,
					wa_phone_number_id,
				})
				.then((resp) => resp.data)
		},
		async getLeadPurposes(params?: unknown): Promise<Array<ILeadPurpose>> {
			return xhr
				.get("/trip-plan-request-lead-purposes", { params })
				.then((resp) => resp.data.data)
		},
		async updateLead(
			id: string | number,
			data: unknown
		): Promise<ITripPlanRequest> {
			return xhr.patch(`/leads/${id}`, data).then((resp) => resp.data.data)
		},
		async getLeadCloseReasons(
			params?: unknown
		): Promise<Array<{ id: number; name: string }>> {
			return xhr
				.get("/lead-close-reasons", { params })
				.then((resp) => resp.data.data)
		},
		async closeLead(data: {
			id: number
			lead_channel: number
			lead_purpose: number
			lead_close_reason: number
		}): Promise<{ message: string }> {
			return xhr
				.delete(`/leads/${data.id}`, {
					params: {
						lead_channel_id: data.lead_channel,
						lead_purpose_id: data.lead_purpose,
						lead_close_reason_id: data.lead_close_reason,
					},
				})
				.then((resp) => resp.data)
		},
	}
}
